import CCColors from '@/Utils/CCColors';
import React, {useState} from 'react';
import {
  TouchableOpacity,
  View,
  Text,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {styles} from './styles';
import SlideModal from '@/Atoms/SlideModal';
import useManageParticipants from '@/Hooks/ManageStudyParticipantsApi/useManageParticipants';
import {useToast} from '@/toast/useToast';

const ROLE_OPTIONS = [
  {
    key: 'contributor',
    label: 'Contributor',
    icon: 'pencil-outline',
    desc: 'Can add moves, chapters and notes',
  },
  {
    key: 'viewer',
    label: 'Viewer',
    icon: 'eye-outline',
    desc: 'Can watch and follow along',
  },
] as const;

const Addedparticipants = ({
  isOwner,
  gameId,
  setLoading = () => {},
  refetchStudy,
}: {
  isOwner: boolean;
  gameId: string;
  setLoading?: (v: boolean) => void;
  refetchStudy?: () => void;
}) => {
  const [addNewPlayer, setAddNewPlayer] = useState(false);
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'contributor' | 'viewer'>('contributor');
  const [errors, setErrors] = useState({flag: false, msg: ''});
  const [isAddHovered, setIsAddHovered] = useState(false);
  const showToast = useToast();

  const {mutate: manageParticipants, isLoading} = useManageParticipants(
    () => {
      setLoading(false);
      setAddNewPlayer(false);
      setPhone('');
      refetchStudy?.();
      showToast('Participant added successfully', {type: 'Success'});
    },
    (error: any) => {
      setLoading(false);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        'Failed to add participant. Check the number and try again.';
      setErrors({flag: true, msg});
      showToast(msg, {type: 'Error'});
    },
  );

  const handleSubmit = () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      setErrors({flag: true, msg: 'Please enter a mobile number.'});
      return;
    }
    setErrors({flag: false, msg: ''});
    setLoading(true);
    manageParticipants({phone: trimmed, action: 'add', gameId, role});
  };

  return (
    <View>
      {isOwner && (
        <View style={styles.stickyButtonWrapper}>
          <TouchableOpacity
            style={[
              styles.addNewBox,
              isAddHovered && {
                borderColor: 'rgba(165,121,47,0.4)',
                backgroundColor: '#FFFDF6',
              },
            ]}
            onHoverIn={() => setIsAddHovered(true)}
            onHoverOut={() => setIsAddHovered(false)}
            onPress={() => {
              setPhone('');
              setRole('contributor');
              setErrors({flag: false, msg: ''});
              setAddNewPlayer(true);
            }}>
            <MaterialCommunityIcons
              name="account-plus-outline"
              size={15}
              color={
                isAddHovered
                  ? CCColors.Workbook.GoldText
                  : CCColors.Workbook.Brown
              }
            />
            <Text
              style={{
                fontSize: 13,
                fontWeight: '600',
                color: isAddHovered
                  ? CCColors.Workbook.GoldText
                  : CCColors.Workbook.Brown,
                marginLeft: 6,
              }}>
              Add Participant
            </Text>
          </TouchableOpacity>
        </View>
      )}

      <SlideModal
        visible={addNewPlayer}
        onClose={() => {
          if (!isLoading) {
            setAddNewPlayer(false);
            setPhone('');
            setErrors({flag: false, msg: ''});
          }
        }}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: 440,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>
        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              flex: 1,
              marginRight: 24,
            }}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons
                name="chess-pawn"
                size={18}
                color="#FFFFFF"
              />
            </View>
            <View style={{flex: 1}}>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 'bold',
                  color: '#FFFFFF',
                  letterSpacing: -0.2,
                }}>
                Invite a Player
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                Bring another player to this side of the board.
              </Text>
            </View>
          </View>

          {/* Close button */}
          <TouchableOpacity
            onPress={() => {
              if (!isLoading) {
                setAddNewPlayer(false);
                setPhone('');
                setErrors({flag: false, msg: ''});
              }
            }}
            activeOpacity={0.8}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close-outline" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {/* Body container */}
        <View style={{padding: 24, gap: 18}}>
          {/* Mobile Number Input */}
          <View style={{gap: 6}}>
            <Text
              style={{
                fontSize: 10,
                fontWeight: 'bold',
                color: '#9E9E9E',
                letterSpacing: 0.5,
              }}>
              PLAYER'S MOBILE NUMBER
            </Text>
            <TextInput
              value={phone}
              autoFocus
              placeholder="e.g. +1 555 123 4567"
              placeholderTextColor="#A39081"
              onChangeText={text => {
                setErrors({flag: false, msg: ''});
                setPhone(text);
              }}
              style={{
                height: 38,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: errors?.flag ? '#FF4D4D' : '#E6E6E6',
                backgroundColor: '#FFFFFF',
                paddingHorizontal: 12,
                fontSize: 12,
                color: '#1E110A',
              }}
            />
            {errors?.flag && (
              <Text style={{fontSize: 9, color: '#FF4D4D', fontWeight: '500'}}>
                {errors?.msg}
              </Text>
            )}
          </View>

          {/* Role selection */}
          <View style={{gap: 8}}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <MaterialCommunityIcons
                name="chess-knight"
                size={13}
                color={CCColors.Workbook.Brown}
              />
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 'bold',
                  color: '#9E9E9E',
                  letterSpacing: 0.5,
                }}>
                ROLE
              </Text>
            </View>
            <View style={{flexDirection: 'row', gap: 8}}>
              {ROLE_OPTIONS.map(opt => {
                const isActive = role === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    onPress={() => setRole(opt.key)}
                    activeOpacity={0.8}
                    style={{
                      flex: 1,
                      borderRadius: 10,
                      borderWidth: 1.5,
                      padding: 12,
                      gap: 4,
                      borderColor: isActive ? '#B58E58' : '#EAE2D5',
                      backgroundColor: isActive ? '#FFFDF8' : '#FFFFFF',
                    }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                      <MaterialCommunityIcons
                        name={opt.icon}
                        size={14}
                        color={isActive ? '#B58E58' : '#757575'}
                      />
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: 'bold',
                          color: isActive ? '#B58E58' : '#2C1A11',
                          flex: 1,
                        }}>
                        {opt.label}
                      </Text>
                      {isActive && (
                        <MaterialCommunityIcons
                          name="check-circle"
                          size={13}
                          color="#B58E58"
                        />
                      )}
                    </View>
                    <Text
                      style={{fontSize: 10.5, color: '#757575', marginTop: 2}}>
                      {opt.desc}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>

        {/* Footer */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: 10,
            paddingHorizontal: 24,
            paddingBottom: 20,
            paddingTop: 14,
            borderTopWidth: 1,
            borderColor: '#F5F5F5',
          }}>
          <TouchableOpacity
            onPress={() => {
              if (!isLoading) {
                setAddNewPlayer(false);
                setPhone('');
                setErrors({flag: false, msg: ''});
              }
            }}
            activeOpacity={0.8}
            style={{
              borderWidth: 1.5,
              borderColor: '#CCCCCC',
              backgroundColor: '#FFFFFF',
              borderRadius: 100,
              paddingHorizontal: 22,
              paddingVertical: 8,
            }}>
            <Text style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>
              Cancel
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            disabled={isLoading}
            onPress={handleSubmit}
            activeOpacity={0.8}
            style={{
              backgroundColor: isLoading ? '#F5F5F5' : '#FACF47',
              borderRadius: 100,
              paddingHorizontal: 24,
              paddingVertical: 8,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
            {isLoading && <ActivityIndicator size="small" color="#1E110A" />}
            <Text
              style={{
                fontSize: 12,
                fontWeight: 'bold',
                color: isLoading ? '#B0B0B0' : '#1E110A',
              }}>
              {isLoading ? 'Inviting…' : 'Send Invite'}
            </Text>
          </TouchableOpacity>
        </View>
      </SlideModal>
    </View>
  );
};

export default Addedparticipants;
